import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Loader2, RefreshCw, AlertTriangle, CheckCircle2, Search, MapPin, Settings, Sparkles } from 'lucide-react';
import RuleFormModal from './RuleFormModal';

const API = process.env.REACT_APP_BACKEND_URL;

const FREQ_MONTHS = {
  monthly: 1,
  bimonthly: 2,
  quarterly: 3,
  quadrimonthly: 4,
  semiannual: 6,
  annual: 12,
};

const DEFAULT_ENTRY = () => ({
  booking_code: '',
  guest_name: '',
  check_in: '',
  check_out: '',
  adults_count: 0,
  children_count: 0,
  children_exempt_count: 0,
  paying_persons: 0,
  nights_total: 0,
  nights_taxable: 0,
  auto_amount: 0,
  manual_amount: null,
  final_amount: 0,
  notes: '',
});

export default function ReportFormModal({ report, onClose, onSaved }) {
  const isEdit = !!report;
  const [properties, setProperties] = useState([]);
  const [rules, setRules] = useState([]);
  const [selectedComune, setSelectedComune] = useState('');
  const [propertySearch, setPropertySearch] = useState('');
  const [propertyId, setPropertyId] = useState(report?.property_id || '');
  const [periodYear, setPeriodYear] = useState(report?.period_year || new Date().getFullYear());
  const [periodMonths, setPeriodMonths] = useState(report?.period_months || []);
  const [entries, setEntries] = useState(report?.entries || []);
  const [iban, setIban] = useState(report?.iban || '');
  const [beneficiary, setBeneficiary] = useState(report?.beneficiary_name || '');
  const [causale, setCausale] = useState(report?.causale_generated || '');
  const [paymentMethod, setPaymentMethod] = useState(report?.payment_method || 'sepa');
  const [f24CodiceEnte, setF24CodiceEnte] = useState(report?.f24_codice_ente || '');
  const [f24CodiceTributo, setF24CodiceTributo] = useState(report?.f24_codice_tributo || '3936');
  const [f24Sezione, setF24Sezione] = useState(report?.f24_sezione || 'EL');
  const [status, setStatus] = useState(report?.status || 'draft');
  const [paymentReference, setPaymentReference] = useState(report?.payment_reference || '');
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [ownerInfo, setOwnerInfo] = useState(null); // beneficiario proprietario (pay_owner)
  const [ownerSearch, setOwnerSearch] = useState('');
  const [ownerResults, setOwnerResults] = useState([]);
  const [showRuleModal, setShowRuleModal] = useState(false);

  const reloadRules = async () => {
    const token = localStorage.getItem('hw_token');
    const rr = await fetch(`${API}/api/tourist-tax/rules?active_only=true`, { headers: { Authorization: `Bearer ${token}` } });
    if (rr.ok) setRules(await rr.json());
  };

  // Carica properties + rules
  useEffect(() => {
    (async () => {
      const token = localStorage.getItem('hw_token');
      const [pr, rr] = await Promise.all([
        fetch(`${API}/api/properties?include_archived=true&include_suspended=true`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/tourist-tax/rules?active_only=true`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (pr.ok) setProperties(await pr.json());
      if (rr.ok) setRules(await rr.json());
    })();
  }, []);

  // Imposta comune iniziale se in edit
  useEffect(() => {
    if (isEdit && propertyId && properties.length > 0 && !selectedComune) {
      const p = properties.find(pp => pp.id === propertyId);
      if (p) setSelectedComune((p.city || '').trim());
    }
  }, [isEdit, propertyId, properties, selectedComune]);

  // === Beneficiario PROPRIETARIO (stato "Da pagare al proprietario") ===
  const MESI_FULL = ['gennaio','febbraio','marzo','aprile','maggio','giugno','luglio','agosto','settembre','ottobre','novembre','dicembre'];
  const buildOwnerCausale = () => {
    const pname = properties.find(p => p.id === propertyId)?.name || '';
    const ms = [...periodMonths].sort((a, b) => a - b).map(m => MESI_FULL[m - 1]);
    const periodo = ms.length ? `${ms.join('/')} ${periodYear}` : `anno ${periodYear}`;
    return `Riversamento IDS ${pname} ${periodo}`.replace(/\s+/g, ' ').trim();
  };

  useEffect(() => {
    if (status !== 'pay_owner' || !propertyId) { setOwnerInfo(null); return; }
    (async () => {
      try {
        const token = localStorage.getItem('hw_token');
        const r = await fetch(`${API}/api/tourist-tax/owner-contact/${propertyId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const d = await r.json();
        setOwnerInfo(d);
        if (d.found && d.contact) {
          setIban(d.contact.iban || '');
          setBeneficiary(d.contact.name || '');
        }
        setCausale(buildOwnerCausale());
      } catch (e) {
        console.error('owner-contact fetch error:', e);
      }
    })();
  }, [status, propertyId]);

  const searchOwnerContacts = async (q) => {
    setOwnerSearch(q);
    if ((q || '').trim().length < 2) { setOwnerResults([]); return; }
    try {
      const token = localStorage.getItem('hw_token');
      const r = await fetch(`${API}/api/payment-contacts?search=${encodeURIComponent(q)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const d = await r.json();
        setOwnerResults((d.contacts || []).slice(0, 8));
      }
    } catch (e) { console.error(e); }
  };

  const associateOwner = async (contact) => {
    try {
      const token = localStorage.getItem('hw_token');
      const r = await fetch(`${API}/api/tourist-tax/owner-contact/${propertyId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contact_id: contact.id }),
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d?.detail || 'Errore associazione beneficiario');
        return;
      }
      setOwnerInfo(prev => ({ ...(prev || {}), found: true, contact: d.contact, learned: true }));
      setIban(d.contact.iban || '');
      setBeneficiary(d.contact.name || '');
      setCausale(buildOwnerCausale());
      setOwnerResults([]);
      setOwnerSearch('');
      toast.success('Beneficiario proprietario associato — il sistema lo ricorderà per i prossimi report');
    } catch (e) {
      toast.error('Errore associazione beneficiario');
    }
  };

  // Tutti gli immobili sono selezionabili: archiviati/cessati restano disponibili
  // (la tassa va versata anche per periodi in cui erano attivi) ma marcati come tali
  const isInactiveProp = (p) => (p.status !== 'active') || !!p.archived || !!p.suspended;
  const inactiveLabel = (p) => p.archived ? 'Archiviato' : (p.status === 'terminated' ? 'Cessato' : (p.suspended ? 'Sospeso' : 'Non attivo'));

  // Comuni disponibili: TUTTI i comuni distinti delle properties (anche archiviate/cessate)
  const availableComuni = useMemo(() => {
    const set = new Map(); // lowercase → displayName
    for (const p of properties) {
      const city = (p.city || '').trim();
      if (!city) continue;
      if (!set.has(city.toLowerCase())) {
        set.set(city.toLowerCase(), city);
      }
    }
    return Array.from(set.values()).sort((a, b) => a.localeCompare(b, 'it'));
  }, [properties]);

  // Set comuni che hanno una regola attiva (per badge/indicatori)
  const comunesWithRule = useMemo(() => {
    return new Set(rules.filter(r => r.is_active).map(r => (r.comune_name || '').trim().toLowerCase()));
  }, [rules]);

  const selectedComuneHasRule = selectedComune && comunesWithRule.has(selectedComune.trim().toLowerCase());

  // Properties filtrate per comune scelto + ricerca testuale (attivi prima, poi archiviati/cessati)
  const filteredProperties = useMemo(() => {
    if (!selectedComune) return [];
    const cLow = selectedComune.trim().toLowerCase();
    let list = properties.filter(p => (p.city || '').trim().toLowerCase() === cLow);
    if (propertySearch.trim()) {
      const q = propertySearch.trim().toLowerCase();
      list = list.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.code || '').toLowerCase().includes(q) ||
        (p.cir || '').toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => {
      const ia = isInactiveProp(a) ? 1 : 0, ib = isInactiveProp(b) ? 1 : 0;
      if (ia !== ib) return ia - ib;
      return (a.name || '').localeCompare(b.name || '', 'it');
    });
  }, [properties, selectedComune, propertySearch]);

  const selectedProperty = useMemo(() => properties.find(p => p.id === propertyId), [properties, propertyId]);
  const selectedRule = useMemo(() => {
    if (!selectedComune) return null;
    const cName = selectedComune.trim().toLowerCase();
    return rules.find(r => (r.comune_name || '').trim().toLowerCase() === cName && r.is_active);
  }, [rules, selectedComune]);

  // Reset property selezionata se il comune cambia
  useEffect(() => {
    if (selectedComune && propertyId) {
      const p = properties.find(pp => pp.id === propertyId);
      if (p && (p.city || '').trim().toLowerCase() !== selectedComune.trim().toLowerCase()) {
        setPropertyId('');
      }
    }
  }, [selectedComune]);

  // Auto-popolamento IBAN/causale al cambio property/rule
  useEffect(() => {
    if (selectedRule && !isEdit) {
      setIban(selectedRule.iban || '');
      setBeneficiary(selectedRule.beneficiary_name || '');
      setPaymentMethod(selectedRule.payment_method || 'sepa');
      setF24CodiceEnte(selectedRule.f24_codice_ente || '');
      setF24CodiceTributo(selectedRule.f24_codice_tributo || '3936');
      setF24Sezione(selectedRule.f24_sezione || 'EL');
      // Default mesi in base a frequenza
      const months = FREQ_MONTHS[selectedRule.payment_frequency] || 1;
      const now = new Date();
      const m = now.getMonth() + 1;
      // Crea blocco contiguo terminante al mese corrente
      const block = [];
      for (let i = months - 1; i >= 0; i--) {
        const month = m - i;
        if (month > 0) block.push(month);
      }
      if (periodMonths.length === 0) setPeriodMonths(block);
    }
  }, [selectedRule, isEdit]);

  // Calcolo totali
  const totalAuto = useMemo(() => entries.reduce((sum, e) => sum + (parseFloat(e.auto_amount) || 0), 0), [entries]);
  const totalFinal = useMemo(() => entries.reduce((sum, e) => {
    const f = parseFloat(e.final_amount);
    if (Number.isFinite(f)) return sum + f; // 0 è un valore valido (es. Airbnb incassa la tassa)
    const m = parseFloat(e.manual_amount);
    if (Number.isFinite(m)) return sum + m;
    return sum + (parseFloat(e.auto_amount) || 0);
  }, 0), [entries]);

  // Verifica se (mese, giorno) rientra nel periodo gg-mm → gg-mm (gestisce wrap Capodanno)
  const isDateInPeriod = (m, d, startDm, endDm) => {
    const [sd, sm] = (startDm || '01-01').split('-').map(Number);
    const [ed, em] = (endDm || '31-12').split('-').map(Number);
    const v = m * 100 + d, s = sm * 100 + sd, e = em * 100 + ed;
    return s <= e ? (v >= s && v <= e) : (v >= s || v <= e);
  };

  // Calcolo locale di una entry (replica esatta del backend compute_tax_for_entry:
  // notte per notte, tariffe stagionali + override calculation_type/max_nights per periodo)
  const computeEntry = (entry) => {
    if (!selectedRule) return { ...entry, auto_amount: 0, nights_total: 0, nights_taxable: 0 };
    const ci = entry.check_in ? new Date(entry.check_in.substring(0, 10) + 'T00:00:00') : null;
    const co = entry.check_out ? new Date(entry.check_out.substring(0, 10) + 'T00:00:00') : null;
    if (!ci || !co || isNaN(ci) || isNaN(co) || co <= ci) return { ...entry, auto_amount: 0, nights_total: 0, nights_taxable: 0 };
    const nightsTotal = Math.round((co - ci) / (24 * 3600 * 1000));
    const paying = (parseInt(entry.adults_count) || 0) + Math.max(0, (parseInt(entry.children_count) || 0) - (parseInt(entry.children_exempt_count) || 0));
    const tariffs = (selectedRule.seasonal_tariffs && selectedRule.seasonal_tariffs.length > 0)
      ? selectedRule.seasonal_tariffs
      : [{ start_day_month: '01-01', end_day_month: '31-12', amount: 0 }];
    const defaultMax = selectedRule.default_max_nights_taxed || null;

    const perPeriod = new Map();
    let nightsChargedTotal = 0;
    const cur = new Date(ci);
    while (cur < co) {
      const m = cur.getMonth() + 1, d = cur.getDate();
      let idx = tariffs.findIndex(t => isDateInPeriod(m, d, t.start_day_month, t.end_day_month));
      if (idx < 0) idx = 0;
      const t = tariffs[idx];
      if (!perPeriod.has(idx)) {
        perPeriod.set(idx, {
          amount: parseFloat(t.amount) || 0,
          calcType: t.calculation_type || selectedRule.calculation_type || 'per_person_night',
          nights: 0,
          nightsCharged: 0,
          maxNights: t.max_nights_taxed || defaultMax,
        });
      }
      const p = perPeriod.get(idx);
      p.nights += 1;
      if (p.calcType === 'per_person_night' || p.calcType === 'flat_per_night') {
        if (!p.maxNights || p.nightsCharged < p.maxNights) { p.nightsCharged += 1; nightsChargedTotal += 1; }
      }
      cur.setDate(cur.getDate() + 1);
    }

    let total = 0;
    perPeriod.forEach(p => {
      if (p.calcType === 'per_person_night') total += paying * p.nightsCharged * p.amount;
      else if (p.calcType === 'flat_per_night') total += p.nightsCharged * p.amount;
      else if (p.calcType === 'per_person_booking') { total += paying * p.amount; nightsChargedTotal += p.nights; }
      else if (p.calcType === 'flat_per_booking') { total += p.amount; nightsChargedTotal += p.nights; }
    });

    return { ...entry, paying_persons: paying, nights_total: nightsTotal, nights_taxable: nightsChargedTotal, auto_amount: Math.round(total * 100) / 100 };
  };

  const updateEntry = (i, field, val) => {
    setEntries(prev => prev.map((e, idx) => {
      if (idx !== i) return e;
      const updated = { ...e, [field]: val };
      const recomputed = computeEntry(updated);
      // final_amount = manual_amount se presente altrimenti auto_amount
      recomputed.final_amount = recomputed.manual_amount !== null && recomputed.manual_amount !== '' ? parseFloat(recomputed.manual_amount) : recomputed.auto_amount;
      return recomputed;
    }));
  };

  const addEntry = () => setEntries(prev => [...prev, DEFAULT_ENTRY()]);
  const removeEntry = (i) => setEntries(prev => prev.filter((_, idx) => idx !== i));

  const importFromBookings = async () => {
    if (!propertyId || periodMonths.length === 0) {
      toast.error('Seleziona prima immobile e periodo');
      return;
    }
    setImporting(true);
    try {
      const token = localStorage.getItem('hw_token');
      const r = await fetch(`${API}/api/tourist-tax/bookings-for-period?property_id=${propertyId}&year=${periodYear}&months=${periodMonths.join(',')}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) { toast.error('Errore caricamento prenotazioni'); return; }
      const bookings = await r.json();
      if (bookings.length === 0) { toast.info('Nessuna prenotazione nel periodo'); return; }
      // Priorità ai conteggi auto-popolati dai documenti pre-check-in (campo *_auto).
      // Fallback: campi guests_count/children_count del booking
      let bookingsWithDocs = 0;
      let bookingsMissingDocs = 0;
      const newEntries = bookings.map(b => {
        const hasAutoDocs = b.documents_count > 0;
        if (hasAutoDocs) bookingsWithDocs++;
        else bookingsMissingDocs++;
        const adults = hasAutoDocs ? (parseInt(b.adults_count_auto) || 0) : Math.max(0, (parseInt(b.guests_count) || 0) - (parseInt(b.children_count) || 0));
        const children = hasAutoDocs ? (parseInt(b.children_count_auto) || 0) : (parseInt(b.children_count) || 0);
        const exemptKids = hasAutoDocs ? (parseInt(b.children_exempt_count_auto) || 0) : 0;
        return computeEntry({
          booking_id: b.id,
          booking_code: b.booking_code || '',
          guest_name: b.guest_name || '',
          check_in: (b.check_in || '').substring(0, 10),
          check_out: (b.check_out || '').substring(0, 10),
          adults_count: adults,
          children_count: children,
          children_exempt_count: exemptKids,
          paying_persons: 0,
          nights_total: 0,
          nights_taxable: 0,
          auto_amount: 0,
          manual_amount: null,
          final_amount: 0,
          notes: hasAutoDocs && b.guests_with_unknown_dob > 0
            ? `${b.guests_with_unknown_dob} ospite/i senza data di nascita (contati come adulti)`
            : '',
          _has_docs: hasAutoDocs,
          _docs_count: b.documents_count || 0,
        });
      });
      newEntries.forEach(e => { e.final_amount = e.auto_amount; });
      // Dedup: skip prenotazioni già presenti (per booking_id o booking_code)
      let added = 0;
      let skipped = 0;
      setEntries(prev => {
        const existingKeys = new Set();
        prev.forEach(e => {
          if (e.booking_id) existingKeys.add(`id:${e.booking_id}`);
          if (e.booking_code) existingKeys.add(`code:${e.booking_code}`);
        });
        const filtered = newEntries.filter(ne => {
          const idKey = ne.booking_id ? `id:${ne.booking_id}` : null;
          const codeKey = ne.booking_code ? `code:${ne.booking_code}` : null;
          if ((idKey && existingKeys.has(idKey)) || (codeKey && existingKeys.has(codeKey))) {
            skipped++;
            return false;
          }
          added++;
          // Registra le chiavi per evitare anche doppioni interni a newEntries
          if (idKey) existingKeys.add(idKey);
          if (codeKey) existingKeys.add(codeKey);
          return true;
        });
        return [...prev, ...filtered];
      });
      const baseMsg = bookingsMissingDocs > 0
        ? `${added} nuove prenotazioni importate (${bookingsWithDocs} con doc, ${bookingsMissingDocs} senza). ${skipped} già presenti saltate.`
        : `${added} nuove prenotazioni importate (conteggi auto da documenti). ${skipped} già presenti saltate.`;
      if (added === 0 && skipped > 0) {
        toast.info(`Nessuna nuova prenotazione da importare: tutte le ${skipped} prenotazioni del periodo erano già presenti.`);
      } else {
        toast.success(baseMsg);
      }
    } finally { setImporting(false); }
  };

  // Ricalcolo bulk dei conteggi (adulti/bambini/esenti) di TUTTE le entry esistenti
  // basandosi sui documenti pre-check-in (chiamata in batch).
  const recomputeAllChildrenCounts = async () => {
    if (!propertyId || entries.length === 0) {
      toast.error('Nessuna entry da ricalcolare');
      return;
    }
    setImporting(true);
    try {
      const token = localStorage.getItem('hw_token');
      const r = await fetch(`${API}/api/tourist-tax/bookings-for-period?property_id=${propertyId}&year=${periodYear}&months=${periodMonths.join(',')}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) { toast.error('Errore caricamento documenti pre-check-in'); return; }
      const bookings = await r.json();
      // Mappa per booking_id/code → conteggi auto
      const byId = new Map();
      const byCode = new Map();
      bookings.forEach(b => {
        const data = {
          adults: parseInt(b.adults_count_auto) || 0,
          children: parseInt(b.children_count_auto) || 0,
          exempt: parseInt(b.children_exempt_count_auto) || 0,
          has_docs: b.documents_count > 0,
          unknown_dob: b.guests_with_unknown_dob || 0,
        };
        if (b.id) byId.set(b.id, data);
        if (b.booking_code) byCode.set(b.booking_code, data);
      });
      let updated = 0;
      let skipped = 0;
      setEntries(prev => prev.map(e => {
        const match = (e.booking_id && byId.get(e.booking_id)) || (e.booking_code && byCode.get(e.booking_code));
        if (!match || !match.has_docs) {
          skipped++;
          return e;
        }
        updated++;
        const updatedEntry = computeEntry({
          ...e,
          adults_count: match.adults,
          children_count: match.children,
          children_exempt_count: match.exempt,
          notes: match.unknown_dob > 0
            ? `${match.unknown_dob} ospite/i senza data di nascita (contati come adulti)`
            : e.notes,
          _has_docs: true,
        });
        if (updatedEntry.manual_amount === null || updatedEntry.manual_amount === undefined) {
          updatedEntry.final_amount = updatedEntry.auto_amount;
        }
        return updatedEntry;
      }));
      toast.success(`✨ Ricalcolo bulk: ${updated} entry aggiornate dai documenti pre-check-in${skipped > 0 ? `, ${skipped} senza documenti (invariate)` : ''}`);
    } finally { setImporting(false); }
  };

  const save = async () => {
    if (!propertyId) { toast.error('Seleziona un immobile'); return; }
    if (periodMonths.length === 0) { toast.error('Seleziona almeno un mese'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('hw_token');
      const payload = {
        property_id: propertyId,
        period_year: parseInt(periodYear),
        period_months: periodMonths,
        entries,
        payment_method: paymentMethod,
        iban,
        beneficiary_name: beneficiary,
        causale_generated: causale,
        f24_codice_ente: f24CodiceEnte,
        f24_codice_tributo: f24CodiceTributo,
        f24_sezione: f24Sezione,
        status,
        payment_reference: paymentReference,
      };
      let url = `${API}/api/tourist-tax/reports`;
      let method = 'POST';
      if (isEdit) {
        url = `${API}/api/tourist-tax/reports/${report.id}`;
        method = 'PUT';
      }
      const r = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || `Errore (HTTP ${r.status})`);
        return;
      }
      toast.success(isEdit ? 'Report aggiornato' : 'Report creato');
      onSaved();
    } finally { setSaving(false); }
  };

  const toggleMonth = (m) => {
    setPeriodMonths(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m].sort((a, b) => a - b));
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[95vh] overflow-y-auto" data-testid="report-form-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifica report' : 'Nuovo report tassa di soggiorno'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* TESTATA: comune → immobile + periodo */}
          <Card className="p-4 bg-slate-50">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-medium flex items-center gap-1">
                  <MapPin className="h-3 w-3 text-blue-600" />Comune *
                </label>
                <Select value={selectedComune} onValueChange={setSelectedComune} disabled={isEdit}>
                  <SelectTrigger data-testid="comune-select">
                    <SelectValue placeholder={availableComuni.length === 0 ? 'Nessun comune con immobili attivi' : 'Seleziona comune'} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {availableComuni.map(c => {
                      const hasRule = comunesWithRule.has(c.toLowerCase());
                      return (
                        <SelectItem key={c} value={c}>
                          <span className="flex items-center gap-2">
                            {c}
                            {hasRule ? (
                              <CheckCircle2 className="h-3 w-3 text-emerald-600" aria-label="ha regola" />
                            ) : (
                              <AlertTriangle className="h-3 w-3 text-amber-500" aria-label="nessuna regola" />
                            )}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {selectedRule && (
                  <div className="mt-2 p-2 bg-emerald-50 border border-emerald-300 rounded text-[11px]">
                    <div className="flex items-start gap-1.5 text-emerald-900">
                      <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        Regola: <strong>{
                          selectedRule.calculation_type === 'per_person_booking' ? 'forfettario/persona' :
                          selectedRule.calculation_type === 'flat_per_booking' ? 'forfettario/alloggio' :
                          selectedRule.calculation_type === 'flat_per_night' ? 'forfettario/notte' :
                          'per persona/notte'
                        }</strong>
                        {' · '}frequenza {selectedRule.payment_frequency}
                        {selectedRule.tax_not_applicable && <span className="ml-1 text-red-700 font-semibold">(esente)</span>}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-6 text-[10px] px-2 bg-white border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                        onClick={() => setShowRuleModal(true)}
                        data-testid="edit-rule-modal-inline"
                      >
                        <Settings className="h-3 w-3 mr-1" />
                        Modifica
                      </Button>
                    </div>
                  </div>
                )}
                {selectedComune && !selectedComuneHasRule && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-300 rounded text-[11px]">
                    <div className="flex items-start gap-1.5 text-amber-900">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <strong>{selectedComune}</strong> non ha ancora una regola attiva.
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs px-2 bg-white"
                        onClick={() => setShowRuleModal(true)}
                        data-testid="open-rule-modal-inline"
                      >
                        <Settings className="h-3 w-3 mr-1" />
                        Configura regola
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-medium">Immobile *</label>
                <Select value={propertyId} onValueChange={setPropertyId} disabled={isEdit || !selectedComune}>
                  <SelectTrigger data-testid="property-select">
                    <SelectValue placeholder={!selectedComune ? 'Scegli prima il comune' : (filteredProperties.length === 0 ? 'Nessun immobile' : 'Seleziona immobile')} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[320px]">
                    <div className="sticky top-0 bg-white p-2 border-b z-10">
                      <div className="relative">
                        <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-400" />
                        <input
                          type="text"
                          value={propertySearch}
                          onChange={(e) => setPropertySearch(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          placeholder="Cerca per nome / codice / CIR..."
                          className="w-full pl-7 pr-2 py-1.5 text-xs border rounded outline-none focus:ring-1 focus:ring-blue-400"
                          data-testid="property-search-input"
                        />
                      </div>
                    </div>
                    {filteredProperties.length === 0 ? (
                      <div className="px-3 py-4 text-xs text-slate-400 text-center">
                        {selectedComune ? 'Nessun immobile in questo comune' : 'Seleziona prima un comune'}
                      </div>
                    ) : (
                      filteredProperties.map(p => (
                        <SelectItem key={p.id} value={p.id} data-testid={`property-option-${p.id}`}>
                          <span className="inline-flex items-center gap-2">
                            {p.name}{p.code ? ` (${p.code})` : ''}
                            {isInactiveProp(p) && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 font-medium">
                                {inactiveLabel(p)}
                              </span>
                            )}
                          </span>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {selectedComune && (
                  <p className="text-[10px] text-slate-500 mt-1">
                    {filteredProperties.length} immobil{filteredProperties.length === 1 ? 'e' : 'i'} in {selectedComune}
                    {filteredProperties.filter(isInactiveProp).length > 0 && ` (di cui ${filteredProperties.filter(isInactiveProp).length} archiviati/cessati)`}
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium">Anno *</label>
                <Input type="number" value={periodYear} onChange={e => setPeriodYear(parseInt(e.target.value) || new Date().getFullYear())} data-testid="period-year" />
              </div>
              <div>
                <label className="text-xs font-medium">Stato</label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Bozza</SelectItem>
                    <SelectItem value="ready_to_pay">Pronto al pagamento</SelectItem>
                    <SelectItem value="pay_owner" data-testid="status-pay-owner">Da pagare al proprietario</SelectItem>
                    <SelectItem value="paid">Pagato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Beneficiario PROPRIETARIO (stato: Da pagare al proprietario) */}
            {status === 'pay_owner' && (
              <div className="mt-3" data-testid="owner-beneficiary-box">
                {ownerInfo?.found && ownerInfo.contact ? (
                  <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg">
                    <p className="text-xs font-semibold text-violet-900">
                      👤 Beneficiario proprietario {ownerInfo.learned ? '(associazione memorizzata)' : '(trovato in rubrica)'}
                    </p>
                    <p className="text-sm text-violet-800 mt-1" data-testid="owner-contact-name">
                      <strong>{ownerInfo.contact.name}</strong>
                      <span className="ml-2 font-mono text-xs">{ownerInfo.contact.iban}</span>
                    </p>
                    <p className="text-[11px] text-violet-600 mt-1">Il bonifico nell'XML SEPA andrà al proprietario invece che al Comune.</p>
                  </div>
                ) : (
                  <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg">
                    <p className="text-xs font-semibold text-amber-900">
                      ⚠️ Proprietario "{ownerInfo?.owner_name || '—'}" non trovato in rubrica beneficiari
                    </p>
                    <p className="text-[11px] text-amber-700 mt-1 mb-2">
                      Cercalo e associalo manualmente: il sistema memorizzerà l'associazione e non la richiederà più.
                    </p>
                    <Input
                      value={ownerSearch}
                      onChange={(e) => searchOwnerContacts(e.target.value)}
                      placeholder="Cerca beneficiario in rubrica (min 2 lettere)..."
                      className="h-8 text-sm"
                      data-testid="owner-contact-search"
                    />
                    {ownerResults.length > 0 && (
                      <div className="mt-1 border rounded-md bg-white divide-y max-h-44 overflow-y-auto">
                        {ownerResults.map(ct => (
                          <button
                            key={ct.id}
                            type="button"
                            onClick={() => associateOwner(ct)}
                            className="w-full text-left px-2 py-1.5 hover:bg-violet-50 text-sm flex items-center justify-between gap-2"
                            data-testid={`owner-contact-option-${ct.id}`}
                          >
                            <span>{ct.name}</span>
                            <span className="font-mono text-[10px] text-slate-500">{ct.iban ? ct.iban.slice(0, 12) + '…' : 'NO IBAN'}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="mt-3">
              <label className="text-xs font-medium">Mesi compresi nel periodo *</label>
              <div className="flex flex-wrap gap-1 mt-1">
                {['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'].map((m, i) => (
                  <button
                    key={i+1}
                    type="button"
                    onClick={() => toggleMonth(i+1)}
                    className={`px-3 py-1 text-xs rounded-full border ${periodMonths.includes(i+1) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                    data-testid={`month-btn-${i+1}`}
                  >
                    {m}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-slate-500 mt-1">
                Mensile = 1 mese, bimestrale = 2 mesi consecutivi (es. Lug+Ago), trimestrale = 3 mesi (es. Gen+Feb+Mar), quadrimestrale = 4 mesi, semestrale = 6 mesi, annuale = 12 mesi.
              </p>
            </div>
          </Card>

          {/* PRENOTAZIONI */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold">📋 Prenotazioni ({entries.length})</h4>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={importFromBookings} disabled={importing || !selectedProperty} data-testid="import-bookings-btn">
                  {importing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                  Importa prenotazioni del periodo
                </Button>
                {entries.length > 0 && (
                  <Button size="sm" onClick={recomputeAllChildrenCounts} disabled={importing} variant="outline" className="border-purple-400 text-purple-700 hover:bg-purple-50" data-testid="bulk-recompute-btn" title="Aggiorna adulti/bambini/esenti di tutte le entry dai documenti pre-check-in">
                    {importing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
                    Ricalcola Bambini (Bulk)
                  </Button>
                )}
                <Button size="sm" onClick={addEntry} className="bg-blue-600 text-white" data-testid="add-entry-btn">
                  <Plus className="h-3 w-3 mr-1" /> Aggiungi riga manuale
                </Button>
              </div>
            </div>
            {entries.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">Nessuna prenotazione. Importa dal sistema o aggiungi manualmente.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 text-slate-600">
                    <tr>
                      <th className="text-left px-2 py-1.5">Codice</th>
                      <th className="text-left px-2 py-1.5">Ospite</th>
                      <th className="text-left px-2 py-1.5">Check-in</th>
                      <th className="text-left px-2 py-1.5">Check-out</th>
                      <th className="text-right px-2 py-1.5">Adulti</th>
                      <th className="text-right px-2 py-1.5">Bambini</th>
                      <th className="text-right px-2 py-1.5">B. esenti</th>
                      <th className="text-right px-2 py-1.5">Notti</th>
                      <th className="text-right px-2 py-1.5">N. tax.</th>
                      <th className="text-right px-2 py-1.5">Auto €</th>
                      <th className="text-right px-2 py-1.5">Manuale €</th>
                      <th className="text-right px-2 py-1.5">Finale €</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e, i) => (
                      <tr key={i} className="border-b">
                        <td className="px-1 py-1"><Input value={e.booking_code} onChange={ev => updateEntry(i, 'booking_code', ev.target.value)} className="h-7 text-xs" /></td>
                        <td className="px-1 py-1"><Input value={e.guest_name} onChange={ev => updateEntry(i, 'guest_name', ev.target.value)} className="h-7 text-xs" /></td>
                        <td className="px-1 py-1"><Input type="date" value={e.check_in} onChange={ev => updateEntry(i, 'check_in', ev.target.value)} className="h-7 text-xs" /></td>
                        <td className="px-1 py-1"><Input type="date" value={e.check_out} onChange={ev => updateEntry(i, 'check_out', ev.target.value)} className="h-7 text-xs" /></td>
                        <td className="px-1 py-1"><Input type="number" value={e.adults_count} onChange={ev => updateEntry(i, 'adults_count', parseInt(ev.target.value) || 0)} className="h-7 text-xs w-14" /></td>
                        <td className="px-1 py-1"><Input type="number" value={e.children_count} onChange={ev => updateEntry(i, 'children_count', parseInt(ev.target.value) || 0)} className="h-7 text-xs w-14" /></td>
                        <td className="px-1 py-1"><Input type="number" value={e.children_exempt_count} onChange={ev => updateEntry(i, 'children_exempt_count', parseInt(ev.target.value) || 0)} className="h-7 text-xs w-14" /></td>
                        <td className="px-1 py-1 text-right font-mono">{e.nights_total}</td>
                        <td className="px-1 py-1 text-right font-mono">{e.nights_taxable}</td>
                        <td className="px-1 py-1 text-right font-mono">€{(e.auto_amount || 0).toFixed(2)}</td>
                        <td className="px-1 py-1"><Input type="number" step="0.01" value={e.manual_amount ?? ''} onChange={ev => updateEntry(i, 'manual_amount', ev.target.value === '' ? null : parseFloat(ev.target.value))} placeholder="—" className="h-7 text-xs w-20" /></td>
                        <td className="px-1 py-1 text-right font-mono font-bold">€{(e.final_amount || 0).toFixed(2)}</td>
                        <td className="px-1 py-1"><Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => removeEntry(i)}><Trash2 className="h-3 w-3" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold bg-slate-50">
                      <td colSpan="9" className="px-2 py-2 text-right">Totali:</td>
                      <td className="px-2 py-2 text-right font-mono">€{totalAuto.toFixed(2)}</td>
                      <td></td>
                      <td className="px-2 py-2 text-right font-mono text-emerald-700">€{totalFinal.toFixed(2)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>

          {/* DATI PAGAMENTO */}
          <Card className="p-4 bg-amber-50/30">
            <h4 className="font-semibold mb-3">💳 Dati Pagamento</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium">Metodo</label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sepa">Bonifico SEPA</SelectItem>
                    <SelectItem value="f24">F24</SelectItem>
                    <SelectItem value="pagopa">PagoPA</SelectItem>
                    <SelectItem value="other">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium">Riferimento Pagamento (CRO/F24/PagoPA)</label>
                <Input value={paymentReference} onChange={e => setPaymentReference(e.target.value)} placeholder="opzionale" />
              </div>
              {paymentMethod === 'sepa' && (
                <>
                  <div>
                    <label className="text-xs font-medium">IBAN Beneficiario</label>
                    <Input value={iban} onChange={e => setIban(e.target.value.replace(/\s/g, '').toUpperCase())} data-testid="report-iban" />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Intestazione</label>
                    <Input value={beneficiary} onChange={e => setBeneficiary(e.target.value)} data-testid="report-beneficiary" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-medium">Causale Bonifico</label>
                    <Input value={causale} onChange={e => setCausale(e.target.value)} data-testid="report-causale" />
                  </div>
                </>
              )}
              {paymentMethod === 'f24' && (
                <>
                  <div>
                    <label className="text-xs font-medium">Codice Ente Comune</label>
                    <Input value={f24CodiceEnte} onChange={e => setF24CodiceEnte(e.target.value.toUpperCase())} placeholder="es. D969 (codice catastale)" data-testid="report-f24-codice-ente" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium">Codice Tributo</label>
                      <Input value={f24CodiceTributo} onChange={e => setF24CodiceTributo(e.target.value)} data-testid="report-f24-codice-tributo" />
                    </div>
                    <div>
                      <label className="text-xs font-medium">Sezione</label>
                      <Input value={f24Sezione} onChange={e => setF24Sezione(e.target.value.toUpperCase())} data-testid="report-f24-sezione" />
                    </div>
                  </div>
                </>
              )}
            </div>
          </Card>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Annulla</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-report-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Salva Report
            </Button>
          </div>
        </div>
      </DialogContent>

      {showRuleModal && (
        <RuleFormModal
          rule={selectedRule || null}
          prefill={selectedRule ? null : { comune_name: selectedComune }}
          onClose={() => setShowRuleModal(false)}
          onSaved={async () => {
            const wasEdit = !!selectedRule;
            setShowRuleModal(false);
            await reloadRules();
            toast.success(wasEdit
              ? `Regola di ${selectedComune} aggiornata`
              : `Regola creata per ${selectedComune}: ora puoi procedere`);
          }}
        />
      )}
    </Dialog>
  );
}
